import { useState } from 'react';
import type { Member } from '@/types';
import { roleLabels } from '@/mocks/organization';

interface EditRoleModalProps {
  member: Member | null;
  onClose: () => void;
  onSave: (memberId: string, newRole: Member['role']) => void;
}

const roleOptions: Member['role'][] = ['org_admin', 'hr_manager', 'recruiter', 'viewer'];

export default function EditRoleModal({ member, onClose, onSave }: EditRoleModalProps) {
  const [role, setRole] = useState<Member['role']>(member?.role || 'recruiter');
  const [saving, setSaving] = useState(false);

  if (!member) return null;

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      onSave(member.id, role);
      setSaving(false);
    }, 500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl border border-background-200 w-full max-w-md shadow-sm">
        <div className="px-5 py-4 border-b border-background-100 flex items-center justify-between">
          <h3 className="text-base font-semibold text-foreground-900">Изменить роль</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center text-foreground-400 transition-colors cursor-pointer"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
              <span className="text-primary-700 font-heading font-semibold">{member.avatar}</span>
            </div>
            <div>
              <p className="font-medium text-foreground-900">{member.name}</p>
              <p className="text-xs text-foreground-400">{member.email}</p>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground-700">Новая роль</label>
            <div className="space-y-2">
              {roleOptions.map((r) => (
                <button
                  key={r}
                  onClick={() => setRole(r)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all cursor-pointer ${
                    role === r
                      ? 'border-primary-300 bg-primary-50/50'
                      : 'border-background-200 hover:border-background-300 bg-white'
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      role === r ? 'border-primary-500' : 'border-background-300'
                    }`}
                  >
                    {role === r && <div className="w-2 h-2 rounded-full bg-primary-500" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{roleLabels[r]}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-background-100 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            Отмена
          </button>
          <button
            onClick={handleSave}
            disabled={saving || role === member.role}
            className="px-4 py-2 rounded-xl text-sm bg-primary-500 text-white hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap flex items-center gap-1.5"
          >
            {saving && <i className="ri-loader-4-line animate-spin" />}
            Сохранить
          </button>
        </div>
      </div>
    </div>
  );
}